import React, { useState, useEffect, useContext } from "react";
import "./ExameList.css";
import { ShopContext } from "../../Context/ShopContext";
import { ClipLoader } from "react-spinners";
import { Link, useLocation } from "react-router-dom";
import ReactPaginate from "react-paginate";
import { generateSlug } from "../../Pages/utils";
import ConfirmModal from "../../Pages/ConfirmModal.jsx";
import clinicaConfig from "../../clinicaConfig.js";
import { ReactComponent as Foto } from "../Assets/ExameFoto.svg";
import { ReactComponent as Urina } from "../Assets/urina.svg";
import { ReactComponent as Sangue } from "../Assets/sangue.svg";
import { ReactComponent as Fezes } from "../Assets/fezes.svg";
import { ReactComponent as TagIcon } from "../Assets/tag_icon.svg";
import { FaSearch, FaShoppingCart, FaWhatsapp } from "react-icons/fa";

const ITEMS_PER_PAGE = 15;

const normalizeText = (text) =>
  text.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
}

const useCSSVariable = (variableName) => {
  const [value, setValue] = useState(() =>
    getComputedStyle(document.documentElement).getPropertyValue(variableName).trim()
  );

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const newValue = getComputedStyle(document.documentElement).getPropertyValue(variableName).trim();
      setValue(newValue);
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class", "style"],
    });

    return () => observer.disconnect();
  }, [variableName]);

  return value;
};

const ExameList = () => {
  const primaryColor = useCSSVariable("--primary");
  const query = useQuery();
  const initialSearchTerm = query.get("search") || "";

  const [allproducts, setAllProducts] = useState([]);
  const [activePromotions, setActivePromotions] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [currentItems, setCurrentItems] = useState([]);
  const [pageCount, setPageCount] = useState(0);
  const [itemOffset, setItemOffset] = useState(0);
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [itemToRemove, setItemToRemove] = useState(null);

  const { addToCart, removeFromCart, cartItems } = useContext(ShopContext);

  const API_BASE_URL = clinicaConfig.REACT_APP_API_BASE_URL || 'http://localhost:4000';

  useEffect(() => {
    const fetchExamsAndPromotions = async () => {
      try {
        setLoading(true);
        setError(null);
        const clientDomain = window.location.hostname;
        const headers = {
          'X-Client-Domain': clientDomain,
          'Content-Type': 'application/json'
        };
        const [productsResponse, promotionsResponse] = await Promise.all([
          fetch(`${API_BASE_URL}/public/products`, { headers }),
          fetch(`${API_BASE_URL}/public/promotions`, { headers }),
        ]);
        if (!productsResponse.ok) throw new Error(`Erro produtos: ${productsResponse.status}`);
        if (!promotionsResponse.ok) throw new Error(`Erro promoções: ${promotionsResponse.status}`);
        const productsData = await productsResponse.json();
        const promotionsData = await promotionsResponse.json();
        if (productsData.success) {
          const produtosOrdenados = productsData.products.sort((a, b) =>
            a.name.localeCompare(b.name)
          );
          setAllProducts(produtosOrdenados);
        } else {
          throw new Error(productsData.message || 'Falha ao buscar produtos.');
        }
        if (promotionsData.success) {
          const hoje = new Date();
          hoje.setHours(0, 0, 0, 0);
          const ativas = promotionsData.promotions.filter(promo => {
            const inicio = new Date(promo.dataInicio);
            const fim = new Date(promo.dataFim);
            const inicioUTC = new Date(Date.UTC(inicio.getFullYear(), inicio.getMonth(), inicio.getDate()));
            const fimUTC = new Date(Date.UTC(fim.getFullYear(), fim.getMonth(), fim.getDate(), 23, 59, 59, 999));
            const hojeUTC = new Date(Date.UTC(hoje.getFullYear(), hoje.getMonth(), hoje.getDate()));
            return hojeUTC >= inicioUTC && hojeUTC <= fimUTC;
          });
          setActivePromotions(ativas);
        } else {
          throw new Error(promotionsData.message || 'Falha ao buscar promoções.');
        }
      } catch (err) {
        console.error("ExameList - Erro:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchExamsAndPromotions();
  }, [API_BASE_URL]);

  useEffect(() => {
    const normalizedSearch = normalizeText(searchTerm);
    const result = allproducts.filter((product) => {
      const name = normalizeText(product.name);
      const category = normalizeText(product.category);
      return name.includes(normalizedSearch) || category.includes(normalizedSearch);
    });
    setFilteredProducts(result);
    setItemOffset(0);
  }, [searchTerm, allproducts]);

  useEffect(() => {
    const endOffset = itemOffset + ITEMS_PER_PAGE;
    setCurrentItems(filteredProducts.slice(itemOffset, endOffset));
    setPageCount(Math.ceil(filteredProducts.length / ITEMS_PER_PAGE));
  }, [itemOffset, filteredProducts]);

  const handlePageClick = (event) => {
    const newOffset = (event.selected * ITEMS_PER_PAGE) % filteredProducts.length;
    setItemOffset(newOffset);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleRemoveClick = (item) => {
    const finalId = item.id || item._id;
    if (cartItems[finalId] === 1) {
      setItemToRemove(item);
      setModalOpen(true);
    } else {
      removeFromCart(finalId);
    }
  };

  const confirmRemoval = () => {
    const finalId = itemToRemove.id || itemToRemove._id;
    removeFromCart(finalId);
    setModalOpen(false);
    setItemToRemove(null);
  };

  const cancelRemoval = () => {
    setModalOpen(false);
    setItemToRemove(null);
  };

  const isExamInActivePromotion = (examId) => {
    for (const promo of activePromotions) {
      if (promo.exames && promo.exames.some(exame => (exame.id === examId || exame._id === examId))) {
        return true;
      }
    }
    return false;
  };

  if (loading) {
    return (
      <div className="examelist-container">
        <div className="flex-center" style={{ minHeight: "50vh" }}>
          <ClipLoader size={80} color={primaryColor} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="examelist-container">
        <div className="error-container">
          <h2>Ops! Ocorreu um erro.</h2>
          <p>{error}</p>
          <button onClick={() => window.location.reload()} className="btn-retry">
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="examelist-container">
      <div className="examelist-header">
        <div className="header-content">
          <h2>Catálogo de Exames</h2>
          <p>{filteredProducts.length} exames disponíveis para você</p>
        </div>
        <div className="search-wrapper">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="O que você está procurando?"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
        </div>
      </div>

      <div className="examelist-body">
        {currentItems.length === 0 ? (
          <div className="empty-state-card">
            <div className="empty-icon-circle">
                <FaSearch />
            </div>
            <h3>Nenhum exame encontrado</h3>
            <p>Não encontramos resultados para "<strong>{searchTerm}</strong>". Verifique a ortografia ou tente outro termo.</p>
            <div className="empty-divider">ou</div>
            <p className="empty-subtext">Podemos ajudar você a encontrar o que precisa através do nosso atendimento:</p>
            <a
              className="btn-whatsapp-modern"
              href={`https://wa.me/${clinicaConfig.whatsapp}?text=Olá,%20não%20encontrei%20o%20exame%20que%20desejo.%20Poderia,%20por%20favor,%20me%20ajudar?`}
              target="_blank"
              rel="noreferrer"
            >
              <FaWhatsapp className="wa-icon" />
              Solicitar ajuda via WhatsApp
            </a>
          </div>
        ) : (
          <div className="exame-grid">
            {currentItems.map((product_item) => {
              const finalId = product_item.id || product_item._id;
              const isInPromotion = isExamInActivePromotion(finalId);
              const quantity = cartItems[finalId] || 0;
              return (
                <div className="exame-card" key={finalId}>
                  <Link to={`/Lista-de-exames/${generateSlug(product_item.name)}`} className="exame-card-link">
                    <div className="card-top">
                        <div className="icon-wrapper">
                            {product_item.category === "Sangue" ? <Sangue /> : 
                             product_item.category === "Fezes" ? <Fezes /> : 
                             product_item.category === "Urina" ? <Urina /> : <Foto />}
                        </div>
                        {isInPromotion && (
                            <div className="badge-promo">
                            <TagIcon /> Promoção
                            </div>
                        )}
                    </div>
                    <div className="card-info">
                        <span className="category-label">{product_item.category}</span>
                        <h3>{product_item.name}</h3>
                        <p className="description">{product_item.description}</p>
                    </div>
                    <div className="card-price">
                        {product_item.old_price && parseFloat(product_item.old_price) > 0 && (
                            <span className="price-value">
                              R$ {parseFloat(product_item.old_price).toFixed(2).replace(".", ",")}
                            </span>
                        )}
                    </div>
                  </Link>
                  <div className="card-actions">
                    {quantity > 0 ? (
                      <div className="qty-selector">
                        <button onClick={() => handleRemoveClick(product_item)}>-</button>
                        <span>{quantity}</span>
                        <button onClick={() => addToCart(finalId)}>+</button>
                      </div>
                    ) : (
                      <button className="btn-add" onClick={() => addToCart(finalId)}>
                        <FaShoppingCart style={{ fontSize: '15px' }} /> Adicionar
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {pageCount > 1 && (
        <ReactPaginate
          breakLabel="..."
          nextLabel=">"
          previousLabel="<"
          onPageChange={handlePageClick}
          pageRangeDisplayed={3}
          marginPagesDisplayed={1}
          pageCount={pageCount}
          containerClassName="modern-pagination"
          activeClassName="active"
          pageClassName="page-item"
          pageLinkClassName="page-link"
          previousClassName="nav-item"
          nextClassName="nav-item"
          renderOnZeroPageCount={null}
        />
      )}

      <ConfirmModal
        isOpen={modalOpen}
        onConfirm={confirmRemoval}
        onCancel={cancelRemoval}
        message={<p>Remover <strong>{itemToRemove?.name}</strong> do carrinho?</p>}
      />
    </div>
  );
};

export default ExameList;